import React, { lazy, Suspense, useEffect } from "react";
import "./layout/main";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  Outlet,
} from "react-router-dom";
import { useThemeMode } from "./context/ThemeContext";
import { useColorMode } from "@chakra-ui/react";

import i18n from "i18next";
import { Provider } from "react-redux";
import { initReactI18next, useTranslation } from "react-i18next";
import languageFa from "constants/language-fa.json";
import "./index.css";

import configureStore from "appRedux/store";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { request } from "services/ApiRequest";
import { HttpStatus } from "constants/HttpStatus";
import URLs from "constants/URLs";
import { showMessage } from "appRedux/actions/Notification";
import { refreshTokenSuccess, userSignOut } from "appRedux/actions/Auth";
import { MESSAGE_STATUSES } from "constants/AppConstants";

import { ConfigProvider } from "antd";
import fa_IR from "antd/locale/fa_IR";

import * as utils from "utils";

import { ShakhsSherkatProvider } from "context/ShakhsContext";
import theme from "theme/theme";
import { ChakraProvider, ColorModeScript } from "@chakra-ui/react";
import { ThemeProvider } from "context/ThemeContext";
import PricingPlans from "views/User/PlansPage";


const SodorCSR = lazy(() => import("views/User/SodorCSR"));

const SherkatShakhsPage = lazy(() => import("views/User/SherkatShakhs"));
const UserProfilePage = lazy(() => import("views/User/UserProfile"));

const Dashboard = lazy(() => import("views/User/Dashboard"));
const PhoneVerification = lazy(() => import("views/Auth/PhoneVerification"));
const SherkatShakhAddEditPage = lazy(
  () => import("views/User/SherkatShakhAddEdit"),
);
const KalaKhadamatPage = lazy(() => import("views/User/KalaKhadamat"));
const KalaKhadamatAddEditPage = lazy(
  () => import("views/User/KalaKhadamatAddEdit"),
);
const TarafHesabPage = lazy(() => import("views/User/TarafHesab"));
const TarafHesabAddEditPage = lazy(
  () => import("views/User/TarafHesabAddEdit"),
);
const SoratHesabPage = lazy(() => import("views/User/SoratHesab"));

const SoratHesabAddEditPage = lazy(
  () => import("views/User/SoratHesabAddEdit"),
);
/* const ResetPasswordPage = lazy(() => import("views/Auth/ResetPassword")); */
const EstelamSorathesabPage = lazy(
  () => import("views/User/EstelamSorathesab"),
);
//const ReportsPage = lazy(() => import("views/User/Reports"));
//const ReportViewer = lazy(() => import("views/ReportViewer"));
const SoratHesabEbtalPage = lazy(() => import("views/User/SoratHesabEbtal"));
/* const PlansPage = lazy(() => import("views/User/PlansPage")); */
const SettingPage = lazy(() => import("views/User/Setting"));
const SettingEditPage = lazy(() => import("views/User/SettingEdit"));
const SoratHesabEslahPage = lazy(() => import("views/User/SoratHesabEslah"));
const SoratHesabBargashiPage = lazy(
  () => import("views/User/SoratHesabBargashi"),
);

const SignIn = lazy(() => import("views/Auth/SignIn"));
const SignUp = lazy(() => import("views/Auth/SignUp"));
/* const TestPage = lazy(() => import("views/TestPage"));
const Test2Page = lazy(() => import("views/Test2")); */

const root = ReactDOM.createRoot(document.getElementById("root"));
const store = configureStore();

i18n.use(initReactI18next).init({
  resources: {
    fa: { translation: languageFa },
  },
  lng: "fa",
  fallbackLng: "fa",

  interpolation: {
    escapeValue: false,
  },
});

const SecureRoute = () => {
  const authStore = useSelector((store) => store.auth, shallowEqual);
  const { authUser, tokenInfo, loggedTime } = authStore;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const errorHandlingOptions = {
    [HttpStatus.BAD_REQUEST]: (errorData) => {
      dispatch(
        showMessage({
          status: MESSAGE_STATUSES.ERROR,
          description:
            typeof errorData === "object"
              ? errorData?.title
              : (errorData ?? t("Alert.Global.Error.message")),
        }),
      );
    },
    [HttpStatus.INTERNAL_SERVER_ERROR]: (errorData) => {
      dispatch(
        showMessage({
          status: MESSAGE_STATUSES.ERROR,
          description:
            typeof errorData === "object"
              ? errorData?.title
              : (errorData ?? t("Alert.Global.Error.message")),
        }),
      );
    },
    [HttpStatus.NOT_FOUND]: () => {
      dispatch(
        showMessage({
          status: MESSAGE_STATUSES.ERROR,
          description: t("Alert.Global.Error.message.not.found"),
        }),
      );
    },
    [HttpStatus.UNAUTHORIZED]: () => {
      navigate("/");
    },
  };

  const refreshTokenOptions = {
    url: URLs.AUTH_TOKEN_GET_BY_REFRESH_TOKEN,
    refreshToken: tokenInfo?.refreshToken,
    callbacks: {
      success: (data) => {
        dispatch(refreshTokenSuccess(data));
      },
      error: () => {
        dispatch(userSignOut());
      },
    },
  };

  request.setRefreshTokenOptions(refreshTokenOptions);
  request.setErrorHandlingOptions(errorHandlingOptions);

  if (
    authUser !== null &&
    loggedTime != null &&
    Number(loggedTime) + Number(tokenInfo.timeout) >
      utils.getCurrentTimeInSecond()
  ) {
    request.setAuthToken(tokenInfo.token);
    return <Outlet />;
  }
  return <Navigate to="/" />;
};

const App = () => {
  const { themeMode } = useThemeMode();

  const { colorMode, toggleColorMode } = useColorMode();
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");

    const syncTheme = () => {
      const shouldBeDark =
        themeMode === "dark" || (themeMode === "system" && media.matches);

      if (shouldBeDark && colorMode !== "dark") toggleColorMode();

      if (!shouldBeDark && colorMode !== "light") toggleColorMode();
    };

    syncTheme();

    media.addEventListener("change", syncTheme);

    return () => media.removeEventListener("change", syncTheme);
  }, [themeMode, colorMode]);
  return (
    <Suspense
      fallback={
        <div>
          <center>
            <div className="loaderText">
              لطفا صبر کنید...
              <div className="loader" />
            </div>
          </center>
        </div>
      }
    >
      <BrowserRouter>
        <Routes>
          {/* <Route path={"/test"} element={<TestPage />} />
          <Route path={"/test2"} element={<Test2Page />} /> */}
          <Route path={"/"} element={<SignIn />} />
          {/* <Route path={"SignIn"} element={<SignInTest />} />
          <Route path={"Signuptest"} element={<SignUpTest />} /> */}
          
          <Route path={"/signup"} element={<SignUp />} />
          <Route element={<SecureRoute />}>
            <Route path={"/user/sodorcsr"} element={<SodorCSR />} />
            {/* <Route path="/user/plans" element={<PricingPlans/>}/> */}

            <Route exact path="/user/dashboard" element={<Dashboard />} />
            <Route exact path="/user/sherkat" element={<SherkatShakhsPage />} />
            <Route
              exact
              path="/user/sherkat/add"
              element={<SherkatShakhAddEditPage />}
            />
            <Route exact path="/user/profile" element={<UserProfilePage />} />
            <Route exact path="/user/kala" element={<KalaKhadamatPage />} />
            <Route
              exact
              path="/user/kala/add"
              element={<KalaKhadamatAddEditPage />}
            />
            <Route exact path="/user/tarafhesab" element={<TarafHesabPage />} />
            <Route
              exact
              path="/user/tarafhesab/add"
              element={<TarafHesabAddEditPage />}
            />
            <Route exact path="/user/sodorhesab" element={<SoratHesabPage />} />

            <Route
              exact
              path="/user/sodorhesab/add"
              element={<SoratHesabAddEditPage />}
            />
            <Route path={"/estelam"} element={<EstelamSorathesabPage />} />
            {/* <Route path={"/reports"} element={<ReportsPage />} /> */}
            <Route
              path={"/user/sodorhesab/ebtal"}
              element={<SoratHesabEbtalPage />}
            />
            <Route
              path={"/user/sodorhesab/eslah"}
              element={<SoratHesabEslahPage />}
            />
            <Route
              path={"/user/sodorhesab/return"}
              element={<SoratHesabBargashiPage />}
            />
            <Route exact path="/setting/edit" element={<SettingEditPage />} />
            <Route path={"/setting"} element={<SettingPage />} />
          </Route>
          <Route path={"/verify"} element={<PhoneVerification />} />
          {/* <Route path={"/resetpassword"} element={<ResetPasswordPage />} /> */}
          {/* <Route path={"/report/:rp/:data"} element={<ReportViewer />} /> */}
          {/* <Route path={"*"} element={<Notfound />} /> */}
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
};

root.render(
  <Provider store={store}>
    <ConfigProvider locale={fa_IR}>
      <ChakraProvider theme={theme}>
        <ColorModeScript initialColorMode={theme.config.initialColorMode} />
        <ThemeProvider>
          <ShakhsSherkatProvider>
            <App />
          </ShakhsSherkatProvider>
        </ThemeProvider>
      </ChakraProvider>
    </ConfigProvider>
  </Provider>,
);
